// Organization Management API - Connected to Backend via API Gateway
// Comprehensive organization management with full CRUD operations

import { authenticatedFetch } from './authenticatedFetch';

// Organization types
export interface Organization {
  id: string;
  name: string;
  slug: string;
  billing_email: string;
  plan_type: 'free' | 'starter' | 'pro' | 'agency' | 'team' | 'professional' | 'enterprise';
  is_active: boolean;
  created_at: string;
  updated_at: string;
  member_count?: number; // Optional - may not be included in all API responses
  // The caller's role within this organization. Populated by /auth/organizations responses.
  user_role?: 'owner' | 'admin' | 'member' | 'viewer';
  settings?: {
    feature_flags?: {
      advanced_analytics?: boolean;
      priority_support?: boolean;
      custom_branding?: boolean;
      api_access?: boolean;
    };
    [key: string]: any;
  };
}

export interface OrganizationMember {
  id: string;
  user_id: string;
  organization_id: string;
  role: 'owner' | 'admin' | 'member' | 'viewer';
  permissions: Record<string, any>;
  invited_at: string;
  joined_at?: string;
  is_active: boolean;
  user: {
    id: string;
    email: string;
    first_name: string;
    last_name: string;
    full_name: string;
    avatar?: string;
    is_active: boolean;
    last_login?: string;
  };
}

export interface CreateOrganizationRequest {
  name: string;
  slug?: string;
  // Optional: matches services/auth/auth_models.py's OrganizationCreate,
  // which defaults billing_email to the requesting user's own email when
  // omitted. Required historically here, but the profile-driven personal
  // workspace flow (Spec P3.3) has no billing email to collect.
  billing_email?: string;
  // The creation endpoint 422s anything but 'free' (spec 20 §5) - paid-plan
  // creation needs a checkout flow that belongs to billing, not this form.
  // Do not widen this back to Organization['plan_type'] above: that type
  // describes organizations that already exist (e.g. provisioned via
  // billing) and legitimately carries paid values; this one describes what
  // callers may ask the endpoint to create.
  plan_type?: 'free';
}

export interface UpdateOrganizationRequest {
  name?: string;
  billing_email?: string;
  settings?: {
    feature_flags?: {
      advanced_analytics?: boolean;
      priority_support?: boolean;
      custom_branding?: boolean;
      api_access?: boolean;
    };
    [key: string]: any;
  };
}

export interface InviteUserRequest {
  email: string;
  role: 'member' | 'admin' | 'viewer';
}

export interface UpdateMemberRoleRequest {
  role: 'owner' | 'admin' | 'member' | 'viewer';
  permissions?: Record<string, any>;
}

// Invitation types (Phase 3)
export interface Invitation {
  id: string;
  email: string;
  role: 'member' | 'admin' | 'viewer';
  status: 'pending' | 'accepted' | 'expired' | 'revoked';
  // Nested, matching the backend's InvitationListItem (services/auth/
  // auth_models.py: `invited_by: Dict[str, str]  # {first_name, last_name}`).
  // Do not flatten to invited_by_name - the same flat-vs-nested mismatch was
  // already fixed once on InvitationDetails below (see AcceptInvitation.tsx).
  invited_by: { first_name: string; last_name: string };
  invited_at: string;
  expires_at: string;
  accepted_at: string | null;
  accepted_by_name: string | null;
}

// Mirrors the backend `InviteDetails` model (auth_models.py). Do not flatten
// these into organization_name / inviter_name - the backend sends nested objects.
export interface InvitationDetails {
  valid: boolean;
  organization: {
    id: string;
    name: string;
    slug: string;
  };
  role: string;
  email: string;
  invited_by: {
    first_name: string;
    last_name: string;
  };
  expires_at: string;
  status: string;
}

export interface RegisterWithInvitationRequest {
  password: string;
  first_name: string;
  last_name: string;
  timezone?: string;
}

// Pending invitation for current user
export interface PendingInvitation {
  id: string;
  organization: {
    id: string;
    name: string;
    slug: string;
  };
  role: 'member' | 'admin' | 'viewer';
  invited_by: {
    first_name: string;
    last_name: string;
  };
  message: string | null;
  invited_at: string;
  expires_at: string;
  token: string;
}

// Wrapper function using shared authenticated fetch
async function apiRequest<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
  return authenticatedFetch<T>(endpoint, { ...options, serviceName: 'Organization API' });
}

// Organization Management API
export const organizationAPI = {
  /**
   * Create a new organization
   */
  async createOrganization(data: CreateOrganizationRequest): Promise<Organization> {
    return await apiRequest<Organization>('/auth/organizations', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  /**
   * Get all organizations for the current user
   */
  async getOrganizations(): Promise<Organization[]> {
    return await apiRequest<Organization[]>('/auth/organizations', {
      method: 'GET',
    });
  },

  /**
   * Get a specific organization by ID
   */
  async getOrganization(organizationId: string): Promise<Organization> {
    return await apiRequest<Organization>(`/auth/organizations/${organizationId}`, {
      method: 'GET',
    });
  },

  /**
   * Update an organization
   */
  async updateOrganization(organizationId: string, data: UpdateOrganizationRequest): Promise<Organization> {
    return await apiRequest<Organization>(`/auth/organizations/${organizationId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  /**
   * Delete an organization (soft delete with 30-day recovery window)
   * Matches backend API: DELETE /auth/organizations/{id}
   */
  async deleteOrganization(organizationId: string): Promise<{
    success: boolean;
    message: string;
    recovery_window_days: number;
  }> {
    return await apiRequest(`/auth/organizations/${organizationId}`, {
      method: 'DELETE',
    });
  },

  /**
   * Invite a user to an organization (Phase 3)
   * Matches backend API: POST /auth/organizations/{id}/invite
   */
  async inviteUserToOrganization(organizationId: string, data: InviteUserRequest): Promise<{
    success: boolean;
    message: string;
    invite_id: string;
    email: string;
    role: string;
    token: string;
    expires_at: string;
    user_exists: boolean;
  }> {
    return await apiRequest(`/auth/organizations/${organizationId}/invite`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  /**
   * Get all members of an organization with pagination and filtering
   * Matches backend API: GET /auth/organizations/{id}/members?limit=X&offset=Y&role=Z
   */
  async getOrganizationMembers(
    organizationId: string,
    options?: { limit?: number; offset?: number; role?: string }
  ): Promise<{
    organization_id: string;
    members: Array<{
      user_id: string;
      email: string;
      first_name: string;
      last_name: string;
      role: 'owner' | 'admin' | 'member' | 'viewer';
      joined_at: string;
      is_active: boolean;
    }>;
    total: number;
    limit: number;
    offset: number;
  }> {
    const params = new URLSearchParams();
    if (options?.limit) params.append('limit', options.limit.toString());
    if (options?.offset) params.append('offset', options.offset.toString());
    if (options?.role) params.append('role', options.role);

    const queryString = params.toString();
    const url = queryString
      ? `/auth/organizations/${organizationId}/members?${queryString}`
      : `/auth/organizations/${organizationId}/members`;

    return await apiRequest(url, {
      method: 'GET',
    });
  },

  /**
   * Update a member's role in an organization
   * Matches backend API: PUT /auth/organizations/{id}/members/{user_id}/role
   */
  async updateMemberRole(organizationId: string, userId: string, data: UpdateMemberRoleRequest): Promise<{
    success: boolean;
    message: string;
    user_id: string;
    new_role: string;
  }> {
    return await apiRequest(`/auth/organizations/${organizationId}/members/${userId}/role`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  /**
   * Remove a member from an organization
   * Matches backend API: DELETE /auth/organizations/{id}/members/{user_id}
   */
  async removeMemberFromOrganization(organizationId: string, userId: string): Promise<{
    success: boolean;
    message: string;
  }> {
    return await apiRequest(`/auth/organizations/${organizationId}/members/${userId}`, {
      method: 'DELETE',
    });
  },

  /**
   * Clear a member's MFA enrollment so they can set it up again.
   * Matches backend API: POST /auth/organizations/{id}/members/{user_id}/reset-mfa
   *
   * For a member who has lost both their authenticator and their backup codes.
   * Nothing can be "sent" to them — backup codes are stored as hashes and the
   * TOTP secret is not recoverable — so the only remedy is clearing it.
   *
   * No request body: the target is entirely in the path, and the server
   * re-derives role and membership rather than trusting the client. The server
   * also refuses self-reset (that would let a stolen session switch MFA off
   * without presenting a factor) and refuses an admin resetting an owner.
   * Those refusals arrive as 400s whose message says what to do instead, so
   * show them verbatim rather than replacing them with a generic error.
   */
  async resetMemberMfa(organizationId: string, userId: string): Promise<{
    success: boolean;
    user_id?: string;
    email?: string;
    mfa_enabled?: boolean;
    message?: string;
  }> {
    return await apiRequest(
      `/auth/organizations/${organizationId}/members/${userId}/reset-mfa`,
      { method: 'POST' },
    );
  },

  /**
   * Get organization usage statistics
   */
  async getOrganizationUsage(organizationId: string, params?: {
    start_date?: string;
    end_date?: string;
    service_name?: string;
  }): Promise<any> {
    const queryParams = new URLSearchParams();
    if (params?.start_date) queryParams.append('start_date', params.start_date);
    if (params?.end_date) queryParams.append('end_date', params.end_date);
    if (params?.service_name) queryParams.append('service_name', params.service_name);
    
    const queryString = queryParams.toString();
    const url = queryString ? `/auth/usage/${organizationId}?${queryString}` : `/auth/usage/${organizationId}`;
    
    return await apiRequest<any>(url, {
      method: 'GET',
    });
  },

  /**
   * Get organization billing information
   */
  async getOrganizationBilling(organizationId: string): Promise<any> {
    return await apiRequest<any>(`/auth/billing/${organizationId}`, {
      method: 'GET',
    });
  },

  /**
   * Get user's organizations with admin perspective
   */
  async getUserOrganizations(): Promise<{
    organizations: Organization[];
    total: number;
    active: number;
    pending: number;
  }> {
    const organizations = await this.getOrganizations();
    return {
      organizations,
      total: organizations.length,
      active: organizations.filter(org => org.is_active).length,
      pending: organizations.filter(org => !org.is_active).length,
    };
  },

  /**
   * Switch to a different organization
   */
  async switchOrganization(organizationId: string): Promise<{
    access_token: string;
    refresh_token: string;
    token_type: string;
    expires_in: number;
    user: any;
  }> {
    return await apiRequest(`/auth/organizations/${organizationId}/switch`, {
      method: 'POST',
      body: JSON.stringify({}),
    });
  },

  /**
   * Restore a soft-deleted organization
   * Matches backend API: POST /auth/organizations/{id}/restore
   */
  async restoreOrganization(organizationId: string): Promise<{
    success: boolean;
    message: string;
  }> {
    return await apiRequest(`/auth/organizations/${organizationId}/restore`, {
      method: 'POST',
    });
  },



  /**
   * Get user activities with pagination and filtering
   * Matches backend API: GET /auth/activities?limit=X&offset=Y&organization_id=Z
   */
  async getActivities(options?: {
    limit?: number;
    offset?: number;
    organization_id?: string;
    activity_types?: string;
    categories?: string;
  }): Promise<{
    activities: Array<{
      id: string;
      user_id: string;
      activity_type: string;
      organization_id: string;
      ip_address: string;
      user_agent: string;
      metadata: Record<string, any>;
      created_at: string;
    }>;
    total: number;
    limit: number;
    offset: number;
  }> {
    const params = new URLSearchParams();
    if (options?.limit) params.append('limit', options.limit.toString());
    if (options?.offset) params.append('offset', options.offset.toString());
    if (options?.organization_id) params.append('organization_id', options.organization_id);
    if (options?.activity_types) params.append('activity_types', options.activity_types);
    if (options?.categories) params.append('categories', options.categories);

    const queryString = params.toString();
    const url = queryString ? `/auth/activities?${queryString}` : `/auth/activities`;

    return await apiRequest(url, {
      method: 'GET',
    });
  },

  /**
   * Get activity statistics
   * Matches backend API: GET /auth/activities/stats?organization_id=X
   */
  async getActivityStats(options?: { organization_id?: string }): Promise<{
    total_activities: number;
    activities_by_type: Record<string, number>;
    recent_activity_count: number;
    last_activity_at: string;
  }> {
    const params = new URLSearchParams();
    if (options?.organization_id) params.append('organization_id', options.organization_id);

    const queryString = params.toString();
    const url = queryString ? `/auth/activities/stats?${queryString}` : `/auth/activities/stats`;

    return await apiRequest(url, {
      method: 'GET',
    });
  },

  // ==================== INVITATION MANAGEMENT (Phase 3) ====================

  /**
   * Get all invitations for an organization
   * Matches backend API: GET /auth/organizations/{org_id}/invitations
   */
  async getInvitations(
    organizationId: string,
    options?: { status?: string; limit?: number; offset?: number }
  ): Promise<{
    invitations: Invitation[];
    total: number;
    limit: number;
    offset: number;
  }> {
    const params = new URLSearchParams();
    if (options?.status) params.append('status', options.status);
    if (options?.limit) params.append('limit', options.limit.toString());
    if (options?.offset) params.append('offset', options.offset.toString());

    const queryString = params.toString();
    const url = queryString
      ? `/auth/organizations/${organizationId}/invitations?${queryString}`
      : `/auth/organizations/${organizationId}/invitations`;

    return await apiRequest(url, {
      method: 'GET',
    });
  },

  /**
   * Get invitation details by token (public endpoint)
   * Matches backend API: GET /auth/invitations/{token}
   */
  async getInvitationDetails(token: string): Promise<InvitationDetails> {
    return await apiRequest(`/auth/invitations/${token}`, {
      method: 'GET',
    });
  },

  /**
   * Accept invitation (existing user)
   * Matches backend API: POST /auth/invitations/{token}/accept
   */
  async acceptInvitation(token: string): Promise<{
    success: boolean;
    message: string;
    organization: {
      id: string;
      name: string;
      slug: string;
      role: string;
    };
  }> {
    return await apiRequest(`/auth/invitations/${token}/accept`, {
      method: 'POST',
      body: JSON.stringify({}),
    });
  },

  /**
   * Register with invitation (new user)
   * Matches backend API: POST /auth/invitations/{token}/register
   */
  async registerWithInvitation(
    token: string,
    data: RegisterWithInvitationRequest
  ): Promise<{
    success: boolean;
    message: string;
    user: {
      id: string;
      email: string;
      first_name: string;
      last_name: string;
      is_active: boolean;
      email_verified: boolean;
    };
    organization: {
      id: string;
      name: string;
      slug: string;
    };
    role: string;
    access_token: string;
    refresh_token: string;
    token_type: string;
  }> {
    return await apiRequest(`/auth/invitations/${token}/register`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  /**
   * Register with invitation using Google OAuth (new user)
   * Matches backend API: POST /auth/invitations/{token}/google
   * Note: The Google account email MUST match the invitation email
   */
  async registerWithInvitationGoogle(
    token: string,
    data: { code: string; redirect_uri: string }
  ): Promise<{
    success: boolean;
    message: string;
    user: {
      id: string;
      email: string;
      first_name: string;
      last_name: string;
      is_active: boolean;
      email_verified: boolean;
    };
    organization: {
      id: string;
      name: string;
      slug: string;
    };
    role: string;
    access_token: string;
    refresh_token: string;
    token_type: string;
  }> {
    return await apiRequest(`/auth/invitations/${token}/google`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  /**
   * Revoke invitation
   * Matches backend API: DELETE /auth/organizations/{org_id}/invitations/{invite_id}
   */
  async revokeInvitation(organizationId: string, inviteId: string): Promise<{
    success: boolean;
    message: string;
  }> {
    return await apiRequest(`/auth/organizations/${organizationId}/invitations/${inviteId}`, {
      method: 'DELETE',
    });
  },

  /**
   * Resend invitation
   * Matches backend API: POST /auth/organizations/{org_id}/invitations/{invite_id}/resend
   */
  async resendInvitation(organizationId: string, inviteId: string): Promise<{
    success: boolean;
    message: string;
    expires_at: string;
  }> {
    return await apiRequest(`/auth/organizations/${organizationId}/invitations/${inviteId}/resend`, {
      method: 'POST',
      body: JSON.stringify({}),
    });
  },

  /**
   * Get pending invitations for the current user
   * Matches backend API: GET /auth/invitations/pending
   */
  async getPendingInvitations(): Promise<{
    invitations: PendingInvitation[];
    total: number;
  }> {
    return await apiRequest('/auth/invitations/pending', {
      method: 'GET',
    });
  },

  /**
   * Decline an invitation (for current user)
   * Matches backend API: POST /auth/invitations/{token}/decline
   */
  async declineInvitation(token: string): Promise<{
    success: boolean;
    message: string;
  }> {
    return await apiRequest(`/auth/invitations/${token}/decline`, {
      method: 'POST',
      body: JSON.stringify({}),
    });
  },
};